import {useRef} from 'react'
import {getLogger} from '../services/logging'
import ReactGA from 'react-ga4'
import {useLazyGetBookingConfirmationQuery} from '../services/ibeApi'

// polling interval in ms and max attempts
const DEFAULT_POLL_INTERVAL = 15000
const DEFAULT_POLL_MAX_ATTEMPTS = 10

const logger = getLogger('useBookingConfirmationPoller')

export interface BookingConfirmationPollingOptions {
  paymentSessionId: string
  onBookingSuccess: (bookingId: string) => void
  onBookingFailure: () => void
  intervalMs?: number
  maxAttempts?: number
}

export const useBookingConfirmationPoller = () => {
  const pollAttempts = useRef(0)
  const [getBookingConfirmation] = useLazyGetBookingConfirmationQuery()

  const pollForBookingConfirmation = async ({
    paymentSessionId,
    onBookingSuccess,
    onBookingFailure,
    intervalMs = DEFAULT_POLL_INTERVAL,
    maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS
  }: BookingConfirmationPollingOptions): Promise<void> => {
    if (!paymentSessionId) {
      return
    }

    const intervalId = setInterval(async () => {
      logger.info({
        message: `Polling for booking confirmation - ${paymentSessionId}`
      })

      if (pollAttempts.current >= maxAttempts) {
        logger.info({
          message:
            'Max polling attempts reached, stop polling for booking confirmation'
        })
        clearInterval(intervalId)
        onBookingFailure()
        return
      } else {
        pollAttempts.current += 1
      }

      try {
        const response = await getBookingConfirmation(paymentSessionId)
        const bookingStatus: string = response.data?.bookingStatus
        const bookingId: string = response.data?.bookingId

        if (!bookingStatus) {
          logger.error({
            message: 'Booking status not found in response',
            data: {response}
          })
          throw new Error('Booking status not found')
        }

        if (bookingStatus === 'created' && bookingId) {
          logger.info({
            message: 'Booking succeeded, stop polling for status',
            data: {bookingId}
          })

          ReactGA.event({
            category: 'IBE',
            action: 'Checkout_Booking_Success',
            value: 1
          })

          onBookingSuccess(bookingId)

          clearInterval(intervalId)
        } else if (bookingStatus === 'failed') {
          logger.error({
            message: 'Booking failed, stop polling for status'
          })

          ReactGA.event({
            category: 'IBE',
            action: 'Checkout_Booking_Failure',
            value: 1
          })

          clearInterval(intervalId)
          onBookingFailure()
        } else if (bookingStatus === 'pending') {
          logger.info({message: 'Continue polling for booking confirmation...'})
          ReactGA.event({
            category: 'IBE',
            action: 'Checkout_Booking_Polling',
            value: 1
          })
        }
      } catch (error) {
        logger.error({
          message:
            'Failed to retrieve booking confirmation, stop polling for status',
          errorData: {error}
        })

        ReactGA.event({
          category: 'IBE',
          action: 'Checkout_Booking_Failure',
          value: 1
        })

        clearInterval(intervalId)
        onBookingFailure()
      }
    }, intervalMs)
  }

  return {
    pollForBookingConfirmation
  }
}
